import React, { useState, useEffect } from 'react'
import Modal from './Modal'
import Input from './Input'
import Button from './Button'
import Avatar from './Avatar'
import { FaCopy } from 'react-icons/fa'
import { useFlowium } from '../client/FlowiumContext'
import type { Workspace, WorkspaceMember } from '@flowium/client'
import { copyTextToClipboard, getDisplayLabel, getErrorMessage } from '../utils'
import { selectControlClassName } from './Input'

interface ShareModalProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  workspaceId
}) => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(workspaceId)
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [teamMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>([])
  const [copyFeedback, setCopyFeedback] = useState('')
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null)
  const { client, user } = useFlowium()
  const selectedWorkspace = workspaces.find(workspace => workspace.id === selectedWorkspaceId)
  const canManageMembers = selectedWorkspace?.creator === user?.developer_id

  useEffect(() => {
    if (isOpen) {
      setSelectedWorkspaceId(workspaceId)
      setError(null)
    }
  }, [isOpen, workspaceId])

  useEffect(() => {
    let active = true
    const fetchData = async () => {
      try {
        const [workspacesData, membersData] = await Promise.all([
          client.getWorkspaces(),
          client.getWorkspaceMembers(selectedWorkspaceId)
        ])
        if (active) {
          setWorkspaces(workspacesData)
          setWorkspaceMembers(membersData)
        }
      } catch (err) {
        if (active) setError('Failed to load workspace data')
      }
    }
    if (isOpen) {
      fetchData()
    }
    return () => {
      active = false
    }
  }, [client, isOpen, selectedWorkspaceId])

  const handleInvite = async () => {
    if (!email.trim()) return
    setIsLoading(true)
    setError(null)
    try {
      await client.workspaceInvite(email, selectedWorkspaceId)
      setEmail('')
      // refresh members list
      const members = await client.getWorkspaceMembers(selectedWorkspaceId)
      setWorkspaceMembers(members)
    } catch (err) {
      const message = getErrorMessage(err)
      setError(message || 'Failed to invite user')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveMember = async (member: WorkspaceMember) => {
    setRemovingMemberId(member.developer_id)
    setError(null)
    try {
      await client.workspaceUninvite(
        member.email,
        selectedWorkspaceId,
        member.developer_id
      )
      const members = await client.getWorkspaceMembers(selectedWorkspaceId)
      setWorkspaceMembers(members)
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to remove workspace member')
    } finally {
      setRemovingMemberId(null)
    }
  }

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/browser?workspace=${selectedWorkspaceId}`
    copyTextToClipboard(shareUrl).then((copied) => {
      setCopyFeedback(copied ? 'Copied!' : 'Copy failed')
      setTimeout(() => setCopyFeedback(''), 1500)
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Share Workspace"
      description="Invite collaborators and copy a direct workspace link."
      size="lg"
    >
      <div className="space-y-6">
        <div>
          <label htmlFor="share-workspace" className="mb-2 block text-sm font-medium text-content">Workspace</label>
          <select
            id="share-workspace"
            value={selectedWorkspaceId}
            onChange={(e) => setSelectedWorkspaceId(e.target.value)}
            className={selectControlClassName}
          >
            {workspaces.map((ws) => (
              <option key={ws.id} value={ws.id}>
                {getDisplayLabel(ws.name, 'Untitled workspace')}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
          <Input
            label="Invite by email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            leftIcon="@"
            disabled={!canManageMembers}
          />
          <Button onClick={handleInvite} disabled={!canManageMembers || isLoading || !email.trim()} className="self-end">
            {isLoading ? 'Inviting...' : 'Invite'}
          </Button>
        </div>

        {!canManageMembers && (
          <p className="text-sm text-content-secondary">Only the workspace owner can manage members.</p>
        )}

        {error && <p className="text-sm text-error">{error}</p>}

        <div className="rounded-2xl border border-secondary bg-surface-secondary p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-content">Access model</p>
              <p className="mt-1 text-sm text-content-secondary">
                Access is currently managed by workspace membership. Invite people here to collaborate.
              </p>
            </div>
            <span className="rounded-full border border-secondary px-3 py-1 text-xs font-medium text-content-secondary">
              Private to members
            </span>
          </div>
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-content">Team members</p>
            <span className="text-sm text-content-secondary">{teamMembers.length} total</span>
          </div>
          <div className="rounded-2xl border border-secondary bg-surface">
            {teamMembers.map((member) => (
              <div key={member.developer_id} className="flex items-center justify-between gap-3 border-b border-secondary px-4 py-3 last:border-b-0">
                <div className="flex items-center gap-3">
                  <Avatar
                    label={member.email.charAt(0).toUpperCase()}
                    size={8}
                    color="#52525B"
                  />
                  <div>
                    <p className="text-sm font-medium text-content">{member.email}</p>
                    <p className="text-xs text-content-secondary">{member.is_owner ? 'Owner' : 'Member'}</p>
                  </div>
                </div>
                {canManageMembers && !member.is_owner ? (
                  <Button
                    variant="danger"
                    size="mini"
                    className="min-w-24"
                    onClick={() => handleRemoveMember(member)}
                    disabled={removingMemberId !== null}
                  >
                    {removingMemberId === member.developer_id ? 'Removing...' : 'Remove'}
                  </Button>
                ) : (
                  <span className="rounded-full border border-secondary px-3 py-1 text-xs font-medium text-content-secondary">
                    {member.is_owner ? 'Owner' : 'Member'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <Button
          onClick={handleCopyLink}
          variant="secondary"
          icon={<FaCopy />}
          fullWidth
        >
          {copyFeedback || 'Copy link'}
        </Button>
      </div>
    </Modal>
  )
}

export default ShareModal
